import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type MediaPromoClipsQuery,
  type MediaPromoClipsQueryVariables,
  PromoClipsQuerySort,
  useMediaPromoClipsLazyQuery,
} from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';

import { PROMO_PAGE_SIZE } from '../consts';
import type { PromoListItem, PromoStatusesInfo } from './types';
import { getPromoError, getPromoStatusesInfo, mapPromoClip } from './utils';

export const usePromoClipsList = (
  companyId: string | undefined,
  draftId: string | undefined,
  filters: MediaPromoClipsQueryVariables['filters'] | undefined,
  isDraftLoading: boolean,
  isDropdownOpen: boolean,
  queryKey: string,
  sort: PromoClipsQuerySort
) => {
  const isMountedRef = useRef(true);
  const rowsRef = useRef<PromoListItem[]>([]);
  const selectionOverridesRef = useRef(new Map<string, boolean>());
  const paginationRef = useRef<MediaPromoClipsQuery['result']['paginationInfo'] | undefined>(undefined);
  const queryGenerationRef = useRef(0);
  const currentDraftIdRef = useRef(draftId);
  const requestedQueryKeyRef = useRef<string | undefined>(undefined);
  const nextPagePromiseRef = useRef<Promise<void> | undefined>(undefined);
  const [requestPromoClips] = useMediaPromoClipsLazyQuery({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
  });
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [hasLoadedFirstPage, setHasLoadedFirstPage] = useState(false);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [items, setItems] = useState<PromoListItem[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<MediaPromoClipsQuery['result']['paginationInfo'] | undefined>(
    undefined
  );
  const [queryError, setQueryError] = useState<Error>();
  const [statusesInfo, setStatusesInfo] = useState<PromoStatusesInfo>();

  const replaceRows = useCallback((nextItems: PromoListItem[]) => {
    rowsRef.current = nextItems;
    setItems(nextItems);
  }, []);

  const applyPromoSelection = useCallback(
    (promoId: string, isSelected: boolean) => {
      selectionOverridesRef.current.set(promoId, isSelected);
      replaceRows(rowsRef.current.map((item) => (item.id === promoId ? { ...item, isSelected } : item)));
    },
    [replaceRows]
  );

  const clearPromoSelectionOverrides = useCallback(() => {
    selectionOverridesRef.current.clear();
  }, []);

  const applySelectionOverrides = useCallback(
    (nextItems: PromoListItem[]) =>
      nextItems.map((item) => {
        const override = selectionOverridesRef.current.get(item.id);

        if (typeof override === 'undefined') {
          return item;
        }

        if (item.isSelected === override) {
          selectionOverridesRef.current.delete(item.id);

          return item;
        }

        return { ...item, isSelected: override };
      }),
    []
  );

  const requestPage = useCallback(
    async (page: number, generation: number, isFirstPage: boolean) => {
      const requestDraftId = draftId;

      if (!companyId || !requestDraftId) {
        return;
      }

      try {
        const result = await requestPromoClips({
          variables: {
            companyId,
            draftId: requestDraftId,
            filters,
            pagination: { page, pageSize: PROMO_PAGE_SIZE },
            sort,
          },
        });
        const payload = result.data?.result;

        if (!payload) {
          throw getPromoError();
        }

        if (
          !isMountedRef.current ||
          generation !== queryGenerationRef.current ||
          currentDraftIdRef.current !== requestDraftId
        ) {
          return;
        }

        const pageRows = applySelectionOverrides((payload.result || []).map(mapPromoClip));

        if (isFirstPage) {
          replaceRows(pageRows);
          setStatusesInfo(getPromoStatusesInfo(payload.statusesInfo));
        } else {
          const rowById = new Map(rowsRef.current.map((item) => [item.id, item]));
          pageRows.forEach((item) => rowById.set(item.id, item));
          replaceRows([...rowById.values()]);
        }

        paginationRef.current = payload.paginationInfo;
        setPaginationInfo(payload.paginationInfo);
        setQueryError(undefined);
      } catch (error) {
        if (
          !isMountedRef.current ||
          generation !== queryGenerationRef.current ||
          currentDraftIdRef.current !== requestDraftId
        ) {
          return;
        }

        setQueryError(error instanceof Error ? error : getPromoError());
      } finally {
        if (
          isMountedRef.current &&
          generation === queryGenerationRef.current &&
          currentDraftIdRef.current === requestDraftId &&
          isFirstPage
        ) {
          setIsInitialLoading(false);
          setHasLoadedFirstPage(true);
        }
      }
    },
    [applySelectionOverrides, companyId, draftId, filters, replaceRows, requestPromoClips, sort]
  );

  const requestFirstPage = useCallback(() => {
    if (!companyId || !draftId) {
      return;
    }

    queryGenerationRef.current += 1;
    const generation = queryGenerationRef.current;
    requestedQueryKeyRef.current = queryKey;
    nextPagePromiseRef.current = undefined;
    paginationRef.current = undefined;
    setPaginationInfo(undefined);
    replaceRows([]);
    setQueryError(undefined);
    setHasLoadedFirstPage(false);
    setIsInitialLoading(true);
    requestPage(0, generation, true);
  }, [companyId, draftId, queryKey, replaceRows, requestPage]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    currentDraftIdRef.current = draftId;
  }, [draftId]);

  useEffect(() => {
    if (!isDropdownOpen || isDraftLoading || !draftId || requestedQueryKeyRef.current === queryKey) {
      return;
    }

    requestFirstPage();
  }, [draftId, isDraftLoading, isDropdownOpen, queryKey, requestFirstPage]);

  const onLoadMore = useCallback(() => {
    const pagination = paginationRef.current;

    if (queryError || !pagination?.hasNextPage || nextPagePromiseRef.current) {
      return nextPagePromiseRef.current || Promise.resolve();
    }

    const generation = queryGenerationRef.current;
    setIsNextPageLoading(true);
    const nextPagePromise = requestPage(pagination.page + 1, generation, false).finally(() => {
      if (nextPagePromiseRef.current === nextPagePromise) {
        nextPagePromiseRef.current = undefined;
      }

      if (isMountedRef.current && generation === queryGenerationRef.current) {
        setIsNextPageLoading(false);
      }
    });
    nextPagePromiseRef.current = nextPagePromise;

    return nextPagePromise;
  }, [queryError, requestPage]);

  const onRetry = useCallback(() => {
    requestedQueryKeyRef.current = undefined;
    requestFirstPage();
  }, [requestFirstPage]);

  return {
    hasNextPage: Boolean(paginationInfo?.hasNextPage),
    hasLoadedFirstPage,
    isInitialLoading,
    isNextPageLoading,
    items,
    queryError,
    statusesInfo,
    replaceRows,
    applyPromoSelection,
    clearPromoSelectionOverrides,
    requestFirstPage,
    onLoadMore,
    onRetry,
  };
};
